import React, { useState, useEffect, useCallback } from 'react'
import { retrieveLaunchParams } from '@telegram-apps/sdk-react'
import { IS_SHOW_STARTPARAM_DEBUG } from '@/config/app'

interface SourceStatus {
  value: string | null
  available: boolean
}

interface ParamSources {
  sdkParsed: SourceStatus
  urlHash: SourceStatus
  performanceApi: SourceStatus
  sessionStorage: SourceStatus
  initDataStartParam: SourceStatus
  timestamp: number
}

function extractStartParam(raw: string): string | null {
  const cleaned = raw.replace(/^[^?#]*[?#]/, '').replace(/[?#]/g, '&')
  const params = new URLSearchParams(cleaned)
  return params.get('tgWebAppStartParam') || null
}

function readAllSources(): ParamSources {
  let sdkValue: string | null = null
  let initDataValue: string | null = null
  try {
    const lp = retrieveLaunchParams() as Record<string, unknown>
    sdkValue = (lp.tgWebAppStartParam as string) ?? null
    const initData = lp.initData as Record<string, unknown> | undefined
    initDataValue = (initData?.startParam as string) ?? null
  } catch {
    // SDK not initialized or failed
  }

  const urlValue = extractStartParam(window.location.href)

  let perfValue: string | null = null
  try {
    const navEntry = performance.getEntriesByType(
      'navigation',
    )[0] as PerformanceNavigationTiming
    if (navEntry?.name) perfValue = extractStartParam(navEntry.name)
  } catch {
    // not available
  }

  let storageValue: string | null = null
  try {
    const raw = window.sessionStorage.getItem('tapps/launchParams')
    if (raw) {
      const parsed = JSON.parse(raw)
      if (typeof parsed === 'string') {
        storageValue = extractStartParam(parsed)
      }
    }
  } catch {
    // not available
  }

  return {
    sdkParsed: { value: sdkValue, available: sdkValue !== null },
    urlHash: { value: urlValue, available: urlValue !== null },
    performanceApi: { value: perfValue, available: perfValue !== null },
    sessionStorage: { value: storageValue, available: storageValue !== null },
    initDataStartParam: {
      value: initDataValue,
      available: initDataValue !== null,
    },
    timestamp: Date.now(),
  }
}

function getStartParamForActivation(): string | null {
  try {
    const lp = retrieveLaunchParams() as Record<string, unknown>
    return (lp.tgWebAppStartParam as string) ?? null
  } catch {
    return extractStartParam(window.location.href)
  }
}

function formatTime(ts: number): string {
  const d = new Date(ts)
  return d.toLocaleTimeString()
}

const Dot: React.FC<{ active: boolean }> = ({ active }) => (
  <span
    style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: active ? '#51cf66' : '#ff6b6b',
      marginRight: 6,
      flexShrink: 0,
    }}
  />
)

export const StartParamDebugPanel: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false)
  const [sources, setSources] = useState<ParamSources | null>(null)

  const startParam = getStartParamForActivation()
  const shouldShow = startParam === 'debug' || IS_SHOW_STARTPARAM_DEBUG

  const refresh = useCallback(() => {
    setSources(readAllSources())
  }, [])

  useEffect(() => {
    if (!shouldShow) return
    refresh()
    const id = setInterval(refresh, 2000)
    return () => clearInterval(id)
  }, [shouldShow, refresh])

  if (!shouldShow) return null

  if (collapsed) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: 10,
          left: 10,
          background: '#2d2d2d',
          color: '#fff',
          borderRadius: 20,
          padding: '6px 14px',
          fontSize: 11,
          fontFamily: 'monospace',
          zIndex: 9999,
          cursor: 'pointer',
          border: '1px solid #555',
        }}
        onClick={() => setCollapsed(false)}
      >
        startapp={startParam ?? 'null'}{' '}
        <span style={{ opacity: 0.5 }}>[ + ]</span>
      </div>
    )
  }

  const rows: { label: string; status: SourceStatus }[] = sources
    ? [
        { label: 'SDK parsed', status: sources.sdkParsed },
        { label: 'URL', status: sources.urlHash },
        { label: 'Perf API', status: sources.performanceApi },
        { label: 'SessionStorage', status: sources.sessionStorage },
        { label: 'initData', status: sources.initDataStartParam },
      ]
    : []

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 10,
        left: 10,
        background: '#1a1a1a',
        border: '1px solid #444',
        borderRadius: 8,
        padding: 12,
        fontSize: 11,
        fontFamily: 'monospace',
        zIndex: 9999,
        maxWidth: 300,
        color: '#e0e0e0',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <strong style={{ color: '#7B91FF' }}>StartParam Debug</strong>
        <span
          style={{ cursor: 'pointer', opacity: 0.6, fontSize: 10 }}
          onClick={() => setCollapsed(true)}
        >
          [ - ]
        </span>
      </div>

      {sources && (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <td style={{ padding: '2px 0', whiteSpace: 'nowrap' }}>
                    <Dot active={row.status.available} />
                    {row.label}
                  </td>
                  <td
                    style={{
                      padding: '2px 0 2px 8px',
                      textAlign: 'right',
                      color: row.status.available ? '#51cf66' : '#666',
                    }}
                  >
                    {row.status.value ?? 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div
            style={{
              marginTop: 8,
              fontSize: 9,
              opacity: 0.5,
              textAlign: 'right',
            }}
          >
            last checked: {formatTime(sources.timestamp)}
          </div>
        </>
      )}
    </div>
  )
}
